import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './AdminCheckboxFilter.module.css';
import sharedStyles from '../AdminFilterShared/AdminFilterShared.module.css';
import filterIcon from '@/assets/icons/filter.svg';

export type Option = { key: string; label: string };

type AdminCheckboxFilterProps = {
  label: string;
  options: Option[];
  value: string[];
  onChange: (next: string[]) => void;
  searchable?: {
    placeholder?: string;
    minQueryLength?: number;
    debounceMs?: number;
    loadOptions: (query: string, signal: AbortSignal) => Promise<Option[]>;
  };
};

export const AdminCheckboxFilter = ({
  label,
  options,
  value,
  onChange,
  searchable,
}: AdminCheckboxFilterProps) => {
  const popoverWidth = searchable ? 520 : 220;
  const [isOpen, setIsOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, width: popoverWidth });
  const [searchQuery, setSearchQuery] = useState('');
  const [asyncOptions, setAsyncOptions] = useState<Option[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const isActive = Array.isArray(value) && value.length > 0;

  const mergedOptions = useMemo(() => {
    const result: Option[] = [];
    const seen = new Set<string>();

    [...asyncOptions, ...options].forEach((entry) => {
      if (!entry.key || seen.has(entry.key)) {
        return;
      }
      seen.add(entry.key);
      result.push(entry);
    });

    value.forEach((selectedKey) => {
      if (!selectedKey || seen.has(selectedKey)) {
        return;
      }
      seen.add(selectedKey);
      result.push({ key: selectedKey, label: selectedKey });
    });

    return result;
  }, [asyncOptions, options, value]);

  const updatePopoverPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = popoverWidth;
    const offsetTop = 8;
    const viewportPadding = 12;
    const left = Math.min(
      Math.max(rect.left - 100, viewportPadding),
      window.innerWidth - width - viewportPadding
    );
    setPopoverPosition({ top: rect.bottom + offsetTop, left, width });
  }, [popoverWidth]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    const handleEscape = (ev: KeyboardEvent) => { if (ev.key === 'Escape') setIsOpen(false); };

    updatePopoverPosition();
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', updatePopoverPosition);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', updatePopoverPosition);
    };
  }, [isOpen, updatePopoverPosition]);

  useEffect(() => {
    if (!isOpen || !searchable) {
      setIsLoading((prev) => (prev ? false : prev));
      setAsyncOptions((prev) => (prev.length ? [] : prev));
      return;
    }

    const query = searchQuery.trim();
    const minQueryLength = searchable.minQueryLength ?? 2;
    if (query.length < minQueryLength) {
      setIsLoading((prev) => (prev ? false : prev));
      setAsyncOptions((prev) => (prev.length ? [] : prev));
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => {
      setIsLoading(true);
      searchable
        .loadOptions(query, controller.signal)
        .then((entries) => {
          if (controller.signal.aborted) {
            return;
          }
          setAsyncOptions(
            Array.isArray(entries)
              ? entries.filter((entry) => Boolean(entry?.key && entry?.label))
              : []
          );
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setAsyncOptions([]);
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setIsLoading(false);
          }
        });
    }, searchable.debounceMs ?? 250);

    return () => {
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [isOpen, searchable, searchQuery]);

  const toggleOption = (key: string) => {
    const has = value.includes(key);
    const next = has ? value.filter((v) => v !== key) : [...value, key];
    onChange(next);
  };

  const reset = () => onChange([]);

  return (
    <div className={`${sharedStyles.root} ${isActive ? sharedStyles.rootActive : ''} ${isOpen ? sharedStyles.rootOpen : ''}`}>
      <span>{label}</span>
      <button
        type="button"
        ref={triggerRef}
        className={sharedStyles.trigger}
        onClick={() => setIsOpen((s) => !s)}
        aria-label={`Filter by ${label.toLowerCase()}`}
      >
        <img src={filterIcon} alt="Filter" className={sharedStyles.icon} />
      </button>

      {isOpen && createPortal(
        <div
          className={sharedStyles.popover}
          ref={popoverRef}
          style={{ top: `${popoverPosition.top}px`, left: `${popoverPosition.left}px`, width: `${popoverPosition.width}px` }}
        >
          <div className={styles.controlsRow}>
            {searchable ? (
              <label className={styles.searchBox}>
                <span className={styles.searchIcon}>⌕</span>
                <input
                  type="text"
                  className={styles.searchInput}
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                  }}
                  placeholder={searchable.placeholder ?? 'Search...'}
                />
              </label>
            ) : null}
            <button type="button" className={`${sharedStyles.reset} ${isActive ? sharedStyles.resetActive : ''}`} onClick={reset}>Reset All</button>
          </div>

          {searchable && isLoading ? <div className={styles.hint}>Searching…</div> : null}

          <div className={`${styles.options} ${searchable ? styles.optionsGrid : ''}`}>
            {mergedOptions.map((opt) => (
              <label key={opt.key} className={styles.optionLabel}>
                <input
                  type="checkbox"
                  checked={value.includes(opt.key)}
                  onChange={() => toggleOption(opt.key)}
                />
                {opt.label}
              </label>
            ))}
          </div>

          {searchable && !isLoading && mergedOptions.length === 0 ? (
            <div className={styles.hint}>No matching domains</div>
          ) : null}
        </div>,
        document.body
      )}
    </div>
  );
};

export default AdminCheckboxFilter;
